import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api';

import type { ApprovalConfigFormData } from '../types';

export interface PayrollFilterParams {
  month?: string;
  startMonth?: string;
  endMonth?: string;
  hospitalId?: string;
  name?: string;
  role?: string;
}

const KEYS = {
  all: ['admin'] as const,
  stats: (month?: string, status?: string) => [...KEYS.all, 'stats', month, status] as const,
  payroll: (filters: PayrollFilterParams) => [...KEYS.all, 'payroll', filters] as const,
  therapistDetail: (id: string, month?: string) => [...KEYS.all, 'therapist', id, month] as const,
  pendingReports: (page?: number) => [...KEYS.all, 'pending', page] as const,
  approvalConfigs: () => [...KEYS.all, 'approval-configs'] as const,
};

export function useAdminStats(month?: string, status?: string) {
  return useQuery({
    queryKey: KEYS.stats(month, status),
    queryFn: async () => {
      const { data } = await adminApi.getStats(month, status);
      return data.stats;
    },
  });
}

export function usePayroll(filters: PayrollFilterParams = {}) {
  return useQuery({
    queryKey: KEYS.payroll(filters),
    queryFn: async () => {
      const { data } = await adminApi.getPayroll(filters);
      return { payroll: data.payroll, totals: data.totals, month: data.month, reportTypes: data.reportTypes };
    },
  });
}

export function useTherapistPayrollDetail(therapistId: string | undefined, monthParams?: { month?: string; startMonth?: string; endMonth?: string }) {
  return useQuery({
    queryKey: KEYS.therapistDetail(therapistId!, monthParams?.month ?? monthParams?.startMonth),
    queryFn: async () => {
      const { data } = await adminApi.getTherapistDetail(therapistId!, monthParams);
      return data;
    },
    enabled: !!therapistId,
  });
}

export function usePendingReports(page?: number) {
  return useQuery({
    queryKey: KEYS.pendingReports(page),
    queryFn: async () => {
      const { data } = await adminApi.getPendingReports(page);
      return { reports: data.reports, total: data.total };
    },
  });
}

export function useApproveReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportType, id }: { reportType: string; id: string }) => {
      await adminApi.approveReport(reportType, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useRejectReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportType, id, reason }: { reportType: string; id: string; reason: string }) => {
      await adminApi.rejectReport(reportType, id, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useRePending() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportType, id }: { reportType: string; id: string }) => {
      await adminApi.rePending(reportType, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useBulkApprove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportIds: Array<{ id: string; reportType: string }>) => {
      const { data } = await adminApi.bulkApprove(reportIds);
      return data.approvedCount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useBulkReject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportIds, reason }: { reportIds: Array<{ id: string; reportType: string }>; reason: string }) => {
      const { data } = await adminApi.bulkReject(reportIds, reason);
      return data.rejectedCount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useMarkPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportType, id }: { reportType: string; id: string }) => {
      await adminApi.markPaid(reportType, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useBulkMarkPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportIds: Array<{ id: string; reportType: string }>) => {
      const { data } = await adminApi.bulkMarkPaid(reportIds);
      return data.paidCount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useApprovalConfigs() {
  return useQuery({
    queryKey: KEYS.approvalConfigs(),
    queryFn: async () => {
      const { data } = await adminApi.getApprovalConfigs();
      return data.configs;
    },
  });
}

export function useCreateApprovalConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApprovalConfigFormData) => {
      const { data: result } = await adminApi.createApprovalConfig(data);
      return result.config;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.approvalConfigs() });
    },
  });
}

export function useUpdateApprovalConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, requiresApproval }: { id: string; requiresApproval: boolean }) => {
      const { data } = await adminApi.updateApprovalConfig(id, { requiresApproval });
      return data.config;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.approvalConfigs() });
    },
  });
}

export function useDeleteApprovalConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteApprovalConfig(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.approvalConfigs() });
    },
  });
}
